import { HttpClient, type HttpErrorResponse, HttpParams } from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { type User } from '@inside-hub-app/customer-portal-b2c-client'
import {
  type VehicleDTO,
  type ReplaceDateWithStringType,
  type VehicleDTOExtended as _VehicleDTOExtended,
  type LegacyVehicleDTOExtended as _LegacyVehicleDTOExtended,
  type imgType, type VehicleSpecsDTO, type NewVehicleDTO,
  type VehicleLoanDto,
  type OwnershipListResponseDto,
  type OwnershipResponseDto,
  type OwnershipCreateRequest,
  type OwnershipCreateResponse,
  type OwnershipDriverDto
} from '@inside-hub-app/customer-portal-shared'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'
import { IconService } from '@inside-hub-app/ef-ng-pp-client'
import { catchError, forkJoin, type Observable, of, switchMap, throwError } from 'rxjs'
import { map, shareReplay, tap } from 'rxjs/operators'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { type hr as cpt } from 'efd-cpt-backend-interfaces-ts'
import { type DealerDTO } from './dealers.service'
import { HashingService } from './hashing.service'
import { SharedService } from './shared.service'
import { TokenService } from './token.service'
import { isAfter, isSameDay, toDate } from 'date-fns'
import { LocalStorageService } from './local-storage.service'
import {
  fromLegacyVehicleDetailsToVehicleSpecsDTO,
  fromVehicleSpecsDTOToLegacyVehicle,
  fromLegacyLoanToVehicleLoan,
  fromOwnershipResponseToVehicleDTO,
  fromNewVehicleDtoToOwnershipRequest
} from '../mappers/new-ownership-api.mapper'
export type VehicleDealersDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle.dto.VehicleDealersDTO
export type BrandDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle.dto.BrandDTO
export type ModelDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle.dto.ModelDTO
export type VehicleFrequentDriverDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.ownership.dto.OwnershipVehicleFrequentDriverDto
export type TrackAndTraceVehicleDTO =
  ReplaceDateWithStringType<cpt.emilfreydigital.customer_portal_backend.rest.vehicle.dto.TrackAndTraceVehicleDTO>
export type ReasonEnum =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle.dto.VehicleDeactivateDTO.ReasonEnum
export type EquipmentDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle.dto.EquipmentDTO

export type LegacyVehicleDTOExtended = _LegacyVehicleDTOExtended
export type VehicleDTOExtended = _VehicleDTOExtended

export type FuelPriceDTO =
  cpt.emilfreydigital.customer_portal_backend.rest.vehicle.dto.FuelPriceDTO
export type OwnershipDetailsDTO =
  ReplaceDateWithStringType<cpt.emilfreydigital.customer_portal_backend.rest.ownership.dto.OwnershipDetailsDto>
export type ManualMileageDto =
  cpt.emilfreydigital.customer_portal_backend.rest.ownership.dto.ManualMileageDto
export type VehicleFirstRegistrationDto =
  ReplaceDateWithStringType<cpt.emilfreydigital.customer_portal_backend.rest.vehicles.dto.VehicleFirstRegistrationDto>

export { VehicleStatus } from '@inside-hub-app/customer-portal-shared'

export enum VehicleTab {
  GENERAL = 'general',
  DETAILS = 'details',
  DEALER = 'dealer',
  VEHICLE_HISTORY = 'vehicle-history',
  GALLERY = 'gallery',
  TRACKING = 'tracking'
}

export interface TrackAndTraceVehicleDTOExtended
  extends TrackAndTraceVehicleDTO {
  isHistory?: boolean
  status?: string
}

export interface LegacyVehiclesResponse {
  userVehicles: LegacyVehicleDTOExtended[]
  inactiveVehicles: LegacyVehicleDTOExtended[]
  tntVehicles: TrackAndTraceVehicleDTOExtended[]
}

export interface VehiclesResponse {
  userVehicles: VehicleDTOExtended[]
  inactiveVehicles: VehicleDTOExtended[]
  tntVehicles: TrackAndTraceVehicleDTOExtended[]
}

/**
 * New ownership API response. Overrides the package DTO's track & trace field
 * so its dates are typed as strings (the actual JSON runtime shape), matching
 * the rest of the app and avoiding a Date/string cast in the fetch logic.
 */
export interface OwnershipsListResponse
  extends Omit<OwnershipListResponseDto, 'trackAndTraceVehicles' | 'ownerships'> {
  trackAndTraceVehicles?: TrackAndTraceVehicleDTOExtended[]
  ownerships?: OwnershipResponseDto[]
}

export interface FrequentDriverDialogData {
  mostFrequentDriver: VehicleFrequentDriverDTO
  ownershipId: number
}

export interface LicencePlateResponse {
  licencePlate: string
}

export interface OwnershipDetailsUpdate {
  nextServiceDate: string | null
  leasingExpiryDate: string | null
}

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {
  private readonly imagesService = inject(IconService)
  private readonly http = inject(HttpClient)
  private readonly hashingService = inject(HashingService)
  private readonly tokenService = inject(TokenService)
  private readonly sharedService = inject(SharedService)
  private readonly localStorageService = inject(LocalStorageService)
  private readonly remoteConfigService = inject(EfRemoteConfigurationService<CustomerPortalConfig>)

  private readonly apiUrl = signal<string>(this.remoteConfigService.get('backend.url') as string)
  private readonly isNewOwnershipApiEnabled = signal<boolean>(Boolean(this.remoteConfigService.get('ownership.new-api.enabled')))

  token: string
  user: User

  private currentGetVehicles: Observable<VehiclesResponse> = null

  setVehicleImage (imgData): {
    imgUrl: string
    imageType: imgType
  } {
    if (this.sharedService.stringExists(imgData.imageUrl)) {
      return {
        imgUrl: imgData.imageUrl,
        imageType: 'vehicleimage'
      }
    } else {
      return {
        imgUrl: this.imagesService.logoUrl(imgData.brand),
        imageType: 'brandlogo'
      }
    }
  }

  public getVehicles (
    includeImage?: boolean,
    includeDetails?: boolean,
    withTnt?: boolean
  ): Observable<VehiclesResponse> {
    this.currentGetVehicles ??= this.isNewOwnershipApiEnabled()
      ? this.getVehiclesFromNewApi(includeImage, withTnt)
      : this.getVehiclesFromLegacyApi(includeImage, includeDetails, withTnt)

    return this.currentGetVehicles
  }

  /**
   * Legacy path: fetches the full vehicle list from /vehicle/user-vehicles
   * and enriches each entry via fetchAdditionalVehicleProperties.
   */
  private getVehiclesFromLegacyApi (
    includeImage?: boolean,
    includeDetails?: boolean,
    withTnt?: boolean
  ): Observable<VehiclesResponse> {
    const token = this.tokenService.getToken()
    let params = new HttpParams()
    params = params.append('c', this.hashingService.hashCode(token.cp_id))
    if (includeImage === true) {
      params = params.append('includeImage', 'true')
    }
    if (includeDetails === true) {
      params = params.append('includeDetails', 'true')
    }
    if (withTnt === true) {
      params = params.append('withTnt', 'true')
    }
    return this.http
      .get<LegacyVehiclesResponse>(this.apiUrl() + '/vehicle/user-vehicles', {
      params
    })
      .pipe(
        switchMap((vehicles: LegacyVehiclesResponse) => {
          const mappedUserVehicles$ = (vehicles.userVehicles?.length ?? 0) > 0
            ? forkJoin(
              vehicles.userVehicles.map(vehicle =>
                this.fetchAdditionalVehicleProperties(vehicle)
              )
            )
            : of([] as NewVehicleDTO[])

          return forkJoin({
            userVehicles: mappedUserVehicles$,
            inactiveVehicles: mappedUserVehicles$,
            tntVehicles: of(vehicles.tntVehicles)
          })
        }),

        map(result => ({
          userVehicles: this.setVehicleArray(result.userVehicles, true),
          inactiveVehicles: this.setVehicleArray(result.inactiveVehicles, false),
          tntVehicles: result.tntVehicles
        })),
        shareReplay(1, 100), // buffer for 100ms
        tap({ complete: () => (this.currentGetVehicles = null) })
      )
  }

  /**
   * New ownership API path: fetches ownerships from /v1/ownerships, maps each
   * to the legacy vehicle shape, and enriches only active vehicles with
   * details/loan. Active/inactive are split from the single returned list.
   */
  private getVehiclesFromNewApi (
    includeImage?: boolean,
    withTnt?: boolean
  ): Observable<VehiclesResponse> {
    return this.getV1Ownerships(includeImage, withTnt).pipe(
      switchMap((response: OwnershipsListResponse | OwnershipResponseDto[]) => {
        const ownerships = Array.isArray(response) ? response : (response.ownerships ?? [])
        const tntVehicles = Array.isArray(response) ? [] : (response.trackAndTraceVehicles ?? [])
        const mappedVehicles$ = ownerships.length > 0
          ? forkJoin(
            ownerships.map(item => {
              const vehicle = fromOwnershipResponseToVehicleDTO(item)
              // Inactive vehicles don't fetch details/loan (same convention as
              // fetchAdditionalVehicleProperties — revisit with new endpoints)
              return vehicle.active === true
                ? this.fetchNewVehicleDetails(vehicle)
                : of(vehicle)
            })
          )
          : of([] as NewVehicleDTO[])

        return forkJoin({
          vehicles: mappedVehicles$,
          tntVehicles: of(tntVehicles)
        })
      }),
      map(result => ({
        userVehicles: this.setVehicleArray(result.vehicles, true),
        inactiveVehicles: this.setVehicleArray(result.vehicles, false),
        tntVehicles: result.tntVehicles
      })),
      shareReplay(1, 100),
      tap({ complete: () => (this.currentGetVehicles = null) })
    )
  }

  public addUserVehicle (vehicle: NewVehicleDTO, createVehicle?: boolean): Observable<NewVehicleDTO> {
    if (this.isNewOwnershipApiEnabled() && createVehicle) {
      const payload: OwnershipCreateRequest = fromNewVehicleDtoToOwnershipRequest(vehicle)

      return this.http.post<OwnershipCreateResponse>(
        this.apiUrl() + '/v1/ownerships/',
        payload
      ).pipe(
        switchMap((ownershipResponse) => this.fetchNewVehicleDetails(fromOwnershipResponseToVehicleDTO(ownershipResponse)))
      )
    }

    const mappedLegacyVehicle = fromVehicleSpecsDTOToLegacyVehicle(vehicle)
    return this.http.post<VehicleDTO>(
      this.apiUrl() + '/vehicle/user-vehicles',
      mappedLegacyVehicle
    ).pipe(
      switchMap((vehicle) => this.fetchAdditionalVehicleProperties(vehicle))
    )
  }

  /**
   * Add a company vehicle = set the current user as the ownership's active driver.
   * - flag ON  -> POST /v1/ownerships/{id}/active-driver (OwnershipDriverDto ignored; passes the input vehicle through)
   * - flag OFF -> legacy POST /vehicle/company-vehicles (full upsert)
   */
  public addCompanyVehicle (vehicle: NewVehicleDTO): Observable<NewVehicleDTO> {
    if (this.isNewOwnershipApiEnabled()) {
      if (vehicle.ownershipId == null) {
        return throwError(() => new Error('ownershipId is required to add a company vehicle'))
      }
      // active-driver returns OwnershipDriverDto — not needed: the input vehicle already has
      // vin + ownershipId, and the list is refetched right after, so just pass it through
      return this.http.post<OwnershipDriverDto>(
        `${this.apiUrl()}/v1/ownerships/${vehicle.ownershipId}/active-driver`,
        {}
      ).pipe(
        map(() => vehicle)
      )
    }
    const mappedLegacyVehicle = fromVehicleSpecsDTOToLegacyVehicle(vehicle)
    return this.http.post<VehicleDTO>(
      this.apiUrl() + '/vehicle/company-vehicles',
      mappedLegacyVehicle
    ).pipe(
      switchMap((vehicle) => this.fetchAdditionalVehicleProperties(vehicle))
    )
  }

  /**
   * Remove a company vehicle = remove the current user as the ownership's active driver.
   * - flag ON  -> DELETE /v1/ownerships/{id}/active-driver -> 204 No Content (no body)
   * - flag OFF -> legacy DELETE /vehicle/company-vehicles/{id}
   */
  public removeCompanyVehicle (ownershipId: number | null): Observable<NewVehicleDTO | void> {
    if (this.isNewOwnershipApiEnabled()) {
      if (ownershipId == null) {
        return throwError(() => new Error('ownershipId is required to remove a company vehicle'))
      }
      return this.http.delete<void>(
        `${this.apiUrl()}/v1/ownerships/${ownershipId}/active-driver`
      )
    }
    return this.http.delete<VehicleDTO>(
      this.apiUrl() + '/vehicle/company-vehicles/' + ownershipId.toString()
    ).pipe(
      switchMap((vehicle) => this.fetchAdditionalVehicleProperties(vehicle))
    )
  }

  public updateLicencePlate (vehicle: NewVehicleDTO): Observable<string> {
    if (this.isNewOwnershipApiEnabled() && vehicle.ownershipId != null) {
      const payload = {
        licencePlate: vehicle.licensePlate
      }

      return this.http.patch<LicencePlateResponse>(
        this.apiUrl() + '/v1/ownerships/' + vehicle.ownershipId.toString() + '/licence-plate',
        payload
      ).pipe(
        map(response => response?.licencePlate)
      )
    }

    const mappedLegacyVehicle = fromVehicleSpecsDTOToLegacyVehicle(vehicle)
    let params = new HttpParams()
    const lang = this.sharedService.currentLanguage()
    params = params.append('lang', lang)
    return this.http.put<VehicleDTO>(
      this.apiUrl() + '/vehicle/user-vehicles/licence-plate',
      mappedLegacyVehicle,
      {
        params
      }
    ).pipe(
      map(response => response?.licensePlate)
    )
  }

  public getV1VehicleDetails (vin: string): Observable<VehicleSpecsDTO> {
    return this.http.get<VehicleSpecsDTO>(
      `${this.apiUrl()}/v1/vehicles/${vin}/details`
    )
  }

  public getV1VehicleLoan (vin: string): Observable<VehicleLoanDto> {
    return this.http.get<VehicleLoanDto>(
      `${this.apiUrl()}/v1/vehicles/${vin}/loan`
    )
  }

  /**
   * Fetch vehicle ownerships from the new ownership API.
   * Returns active, inactive (REMOVED/OWNER_CHANGED) and
   * requested-deactivation ownerships in a single list.
   * @param includeImage opt-in to resolved vehicle image URLs (include=images)
   * @param withTnt opt-in to track & trace vehicles (include=trackAndTraceVehicles)
   */
  public getV1Ownerships (
    includeImage?: boolean,
    withTnt?: boolean
  ): Observable<OwnershipsListResponse | OwnershipResponseDto[]> {
    const include: string[] = []
    if (includeImage === true) {
      include.push('images')
    }
    if (withTnt === true) {
      include.push('trackAndTraceVehicles')
    }
    let params = new HttpParams()
    if (include.length > 0) {
      params = params.append('include', include.join(','))
    }
    return this.http.get<OwnershipsListResponse | OwnershipResponseDto[]>(
      `${this.apiUrl()}/v1/ownerships`,
      { params }
    )
  }

  public getDealer (
    id: number,
    includeContacts?: boolean,
    brandName?: string,
    includeGoogleId?: boolean
  ): Observable<DealerDTO> {
    let params = new HttpParams()
    params =
      includeContacts === true
        ? params.append('includeContacts', 'true')
        : params
    params = this.sharedService.stringExists(brandName)
      ? params.append('brandName', 'true')
      : params
    params =
      includeGoogleId === true
        ? params.append('includeGoogleId', 'true')
        : params

    return this.http.get<DealerDTO>(this.apiUrl() + '/dealers/' + id.toString(), {
      params
    })
  }

  public getVehicleDealers (
    vin: string,
    includeContacts: boolean,
    includeChatData: boolean,
    includeGoogleId: boolean
  ): Observable<VehicleDealersDTO> {
    const url = this.apiUrl() + '/vehicle/dealers/' + vin
    let params = new HttpParams()
    params = includeContacts ? params.append('includeContacts', 'true') : params
    params = includeChatData ? params.append('includeChatData', 'true') : params
    params = includeGoogleId ? params.append('includeGoogleId', 'true') : params

    return this.http.get<VehicleDealersDTO>(url, {
      params
    })
  }

  public getUserDealers (
    includeGoogleId?: boolean
  ): Observable<VehicleDealersDTO[]> {
    const url = this.apiUrl() + '/vehicle/dealers'
    let params = new HttpParams()
    if (includeGoogleId === true) {
      params = params.append('includeGoogleId', 'true')
    }
    return this.http.get<VehicleDealersDTO[]>(url, {
      params
    })
  }

  /**
   * Updates preferred vehicle dealers by VIN.
   *
   * @param salesDealerId number
   * @param serviceDealerId number
   */
  public updatePrefferedVehicleDealers (
    vin: string,
    salesDealerId: number,
    serviceDealerId: number
  ): Observable<VehicleDealersDTO> {
    const url =
      this.apiUrl() +
      '/vehicle/dealers/' +
      vin +
      '?includeContacts=true&includeGoogleId=true'
    const params = {
      salesDealerId,
      serviceDealerId
    }
    return this.http.put<VehicleDealersDTO>(url, params)
  }

  /**
   * Update vehicle preferred dealer contacts
   * Updates preferred vehicle dealer contacts by VIN and preferred dealer id.
   * @param vin string
   * @param dealerId number
   * @param salesmanId number
   * @param salesAdvisorId number
   */
  public updateVehiclePrefferedDealerContacts (
    vin: string,
    dealerId: number,
    salesmanId: number,
    salesAdvisorId: number
  ): Observable<VehicleDealersDTO> {
    const url =
      this.apiUrl() +
      '/vehicle/dealers/' +
      vin +
      '/contacts/' +
      dealerId.toString()
    const body = {
      salesmanId,
      salesAdvisorId
    }
    return this.http.put<VehicleDealersDTO>(url, body)
  }

  public getBrands (): Observable<BrandDTO[]> {
    return this.http.get<BrandDTO[]>(this.apiUrl() + '/vehicle/brands').pipe(
      map((brands: BrandDTO[]) => {
        const excludedBrands: string[] =
          this.remoteConfigService.get('excludedBrands')
        return brands?.filter(brand => {
          if (
            this.sharedService.stringExists(brand.name) &&
            excludedBrands?.[0] != null
          ) {
            return (
              excludedBrands.findIndex(
                b => b.toLowerCase().trim() === brand.name.toLowerCase().trim()
              ) === -1
            )
          } else {
            return true
          }
        })
      })
    )
  }

  public getBrandModels (id: number): Observable<ModelDTO[]> {
    return this.http.get<ModelDTO[]>(
      this.apiUrl() + '/vehicle/models/' + id.toString()
    )
  }

  public getFrequentDriver (
    ownershipId: number
  ): Observable<VehicleFrequentDriverDTO> {
    return this.http.get<VehicleFrequentDriverDTO>(
      this.apiUrl() + '/v1/ownerships/' + ownershipId + '/frequent-driver'
    )
  }

  public createFrequentDriver (
    ownershipId: number,
    driver: VehicleFrequentDriverDTO
  ): Observable<VehicleFrequentDriverDTO> {
    return this.http.post<VehicleFrequentDriverDTO>(
      this.apiUrl() + '/v1/ownerships/' + ownershipId + '/frequent-driver',
      driver
    )
  }

  public updateFrequentDriver (
    ownershipId: number,
    driver: VehicleFrequentDriverDTO
  ): Observable<VehicleFrequentDriverDTO> {
    return this.http.put<VehicleFrequentDriverDTO>(
      this.apiUrl() + '/v1/ownerships/' + ownershipId + '/frequent-driver',
      driver
    )
  }

  public deleteFrequentDriver (
    ownershipId: number
  ): Observable<VehicleFrequentDriverDTO> {
    return this.http.delete<VehicleFrequentDriverDTO>(
      this.apiUrl() + '/v1/ownerships/' + ownershipId + '/frequent-driver'
    )
  }

  public requestVehicleRemoval (
    vin: string,
    reason: ReasonEnum,
    language: string
  ): Observable<unknown> {
    let params = new HttpParams()
    params = params.append('lang', language)
    const body = {
      reason
    }
    return this.http.post(
      this.apiUrl() + '/vehicle/user-vehicles/' + vin + '/deactivate',
      body,
      {
        params
      }
    )
  }

  public getVehicleDetails (
    ownershipId: number | null,
    vin?: string,
    includeImage?: boolean,
    lang?: string
  ): Observable<NewVehicleDTO> {
    if (this.isNewOwnershipApiEnabled()) {
      if (ownershipId == null) {
        return throwError(() => new Error('ownershipId is required to fetch vehicle details'))
      }

      return this.getOwnership(ownershipId).pipe(
        map(ownership => fromOwnershipResponseToVehicleDTO(ownership)),
        switchMap(vehicle => {
          return vehicle.active === true
            ? this.fetchNewVehicleDetails(vehicle)
            : of(vehicle)
        })
      )
    }

    if (!this.sharedService.stringExists(vin)) {
      return throwError(() => new Error('vin is required to fetch legacy vehicle details'))
    }

    let params = new HttpParams()
    params = params.append('lang', lang)
    if (includeImage) {
      params = params.append('includeImage', 'true')
    }
    return this.http.get<VehicleDTO>(
      this.apiUrl() + '/vehicle/user-vehicles/' + vin,
      {
        params
      }
    ).pipe(
      switchMap((vehicle) => this.fetchAdditionalVehicleProperties(vehicle))
    )
  }

  public getVehicleNotifications (
    vin?: string
  ): Observable<VehicleFrequentDriverDTO[]> {
    const token = this.tokenService.getToken()
    let params = new HttpParams()
    params = params.append('c', this.hashingService.hashCode(token.cp_id))
    if (this.sharedService.stringExists(vin)) {
      params = params.append('vin', vin)
    }
    return this.http.get<VehicleFrequentDriverDTO[]>(
      this.apiUrl() + '/vehicles-notifications',
      {
        params
      }
    )
  }

  public addNickname (params: {
    vin: string
    nickname: string
  }): Observable<NewVehicleDTO> {
    return this.http.put<VehicleDTO>(
      this.apiUrl() + '/vehicle/user-vehicles/nickname',
      params
    ).pipe(
      switchMap((vehicle) => this.fetchAdditionalVehicleProperties(vehicle))
    )
  }

  public deleteNickname (vin: string): Observable<VehicleFrequentDriverDTO> {
    return this.http.delete<VehicleFrequentDriverDTO>(
      this.apiUrl() + '/vehicle/user-vehicles/nickname/' + vin
    )
  }

  public setVehicleDealer (
    vehicles: VehicleDTOExtended[],
    userDealers: VehicleDealersDTO[]
  ): void {
    if (userDealers != null && vehicles != null) {
      vehicles.forEach(vehicle => {
        for (let i = 0; i < userDealers.length; i++) {
          const dealer = userDealers[i]
          if (
            this.sharedService.stringExists(dealer.vin) &&
            dealer.vin === vehicle.vin
          ) {
            // check if its the same dealer
            if (
              vehicle.dealers?.salesDealer?.id !== dealer.salesDealer?.id ||
              vehicle.dealers?.serviceDealer?.id !== dealer.serviceDealer?.id
            ) {
              vehicle.dealer = dealer.salesDealer
              vehicle.dealers = dealer
            }
            break
          }
        }
      })
    }
  }

  // track trace
  public getVehiclesTNT (): Observable<TrackAndTraceVehicleDTOExtended[]> {
    // return of(TNT) // Mock data
    return this.http.get<TrackAndTraceVehicleDTOExtended[]>(
      this.apiUrl() + '/vehicle/tnt'
    )
  }

  public getFuelPrices (
    fuelType: string,
    zip?: string,
    coords?,
    radius?: number,
    maxItems?: number,
    sort?: string
  ): Observable<FuelPriceDTO[]> {
    // return of(FUEL_PRICES) // Mock data
    let params = new HttpParams()
    params = params.append('fuelType', fuelType)

    if (coords?.latitude != null) {
      params = params.append('lat', coords.latitude)
      params = params.append('lon', coords.longitude)
    } else {
      if (zip != null) {
        params = params.append('zip', zip)
      }
    }

    if (radius != null) {
      params = params.append('radius', radius)
    }

    if (maxItems != null) {
      params = params.append('maxItems', maxItems)
    }

    if (sort != null) {
      params = params.append('sort', sort)
    }

    return this.http.get<FuelPriceDTO[]>(this.apiUrl() + '/vehicle/fuel-prices', {
      params
    })
  }

  setStatus (data: TrackAndTraceVehicleDTOExtended[]): void {
    if (data?.[0] != null) {
      let index = 0
      data.forEach(vehicle => {
        // if the element doesnt have commissionNumber add temporary since its needed for navigation
        if (!this.sharedService.stringExists(vehicle.commissionNumber)) {
          vehicle.commissionNumber =
            'temporaryCommissionNumber-' + String(index)
          index++
        }

        // check for dates, set status
        const now = new Date()
        vehicle.status = undefined

        if (this.sharedService.stringExists(vehicle.registrationDate)) {
          const dateRegistration = toDate(new Date(vehicle.registrationDate))
          if (isAfter(now, dateRegistration) || isSameDay(now, dateRegistration)) {
            vehicle.status = 'registered'
          }
        }

        if (
          this.sharedService.stringExists(vehicle.deliveryDate) &&
          !this.sharedService.stringExists(vehicle.status)
        ) {
          const dateDelivery = toDate(new Date(vehicle.deliveryDate))
          if (isAfter(now, dateDelivery) || isSameDay(now, dateDelivery)) {
            vehicle.status = 'delivered'
          }
        }

        if (
          this.sharedService.stringExists(vehicle.buildDate) &&
          !this.sharedService.stringExists(vehicle.status)
        ) {
          const dateBuild = toDate(new Date(vehicle.buildDate))
          if (isAfter(now, dateBuild) || isSameDay(now, dateBuild)) {
            vehicle.status = 'production'
          }
        }

        if (
          this.sharedService.stringExists(vehicle.orderDate) &&
          !this.sharedService.stringExists(vehicle.status)
        ) {
          const dateOrder = toDate(new Date(vehicle.orderDate))
          if (isAfter(now, dateOrder) || isSameDay(now, dateOrder)) {
            vehicle.status = 'order'
          }
        }
      })
    }
  }

  setVehicleTNTHistory (
    allVehicles: VehicleDTOExtended[],
    inactiveVehicles: VehicleDTOExtended[],
    tnt: TrackAndTraceVehicleDTOExtended[]
  ): void {
    if ((allVehicles?.[0] != null || inactiveVehicles?.[0] != null) && tnt?.[0] != null) {
      allVehicles?.forEach(vehicle => {
        const history = tnt.find(el => {
          return el.vin === vehicle.vin || el.commissionNumber === vehicle.vin
        })
        if (history != null) {
          history.isHistory = true
        }
      })

      inactiveVehicles?.forEach(vehicle => {
        const history = tnt.find(el => {
          return el.vin === vehicle.vin || el.commissionNumber === vehicle.vin
        })
        if (history != null) {
          history.isHistory = true
        }
      })

      // CPEFG - 2231 - don't show already registered vehicles
      tnt.forEach(el => {
        if (el.status === 'registered') {
          el.isHistory = true
        }
      })
    }
  }

  setVehicleArray (
    vehicles: VehicleDTOExtended[],
    isActive: boolean
  ): VehicleDTOExtended[] {
    return vehicles
      ?.filter(vehicle => vehicle.active === isActive)
      ?.map(vehicle => {
        const imgObj = this.setVehicleImage(vehicle)

        return {
          ...vehicle,
          brandPicture: imgObj.imgUrl,
          pictureType: imgObj.imageType,
          leasingInfo: null,
          dealers: {
            serviceDealer: null,
            salesDealer: null,
            purchasedDealer: null
          }
        }
      })
  }

  getVehicleTab (): string {
    try {
      const vehicleNav = this.localStorageService.getVehicleNavigation()
      return this.sharedService.stringExists(vehicleNav)
        ? vehicleNav
        : VehicleTab.GENERAL
    } catch (error) {
      return VehicleTab.GENERAL
    }
  }

  public getOwnershipDetails (id: number): Observable<OwnershipDetailsDTO> {
    return this.http.get<OwnershipDetailsDTO>(
      this.apiUrl() + `/v1/ownerships/${id}/details`
    )
  }

  public getOwnership (id: number): Observable<OwnershipResponseDto> {
    return this.http.get<OwnershipResponseDto>(
      this.apiUrl() + `/v1/ownerships/${id}`
    )
  }

  /**
   * Update CPT-exclusive ownership data (next service & leasing expiry date).
   * - flag ON  -> PATCH /v1/ownerships/{id}/details with `details` only
   *   (both fields are always sent so the unedited one is not nullified; a
   *   field is null only when the user explicitly clears it)
   * - flag OFF -> falls back to the legacy addUserVehicle upsert
   *
   * `vehicle` is only needed for the legacy fallback — once the legacy endpoint
   * is removed, drop it and take `ownershipId` directly.
   */
  public updateOwnershipDetails (
    vehicle: NewVehicleDTO,
    details: OwnershipDetailsUpdate
  ): Observable<OwnershipDetailsDTO | NewVehicleDTO> {
    if (this.isNewOwnershipApiEnabled() && vehicle.ownershipId != null) {
      return this.http.patch<OwnershipDetailsDTO>(
        `${this.apiUrl()}/v1/ownerships/${vehicle.ownershipId}/details`,
        details
      )
    }

    return this.addUserVehicle(vehicle)
  }

  /**
   * Update the ownership's manual mileage.
   * - flag ON  -> PATCH /v1/ownerships/{id}/manual-mileage
   * - flag OFF -> legacy addUserVehicle upsert (drop `vehicle` once legacy is gone)
   */
  public updateManualMileage (
    vehicle: NewVehicleDTO,
    manualMileage: number | null
  ): Observable<ManualMileageDto | NewVehicleDTO> {
    if (this.isNewOwnershipApiEnabled() && vehicle.ownershipId != null) {
      return this.http.patch<ManualMileageDto>(
        `${this.apiUrl()}/v1/ownerships/${vehicle.ownershipId}/manual-mileage`,
        { manualMileage }
      )
    }
    return this.addUserVehicle(vehicle)
  }

  /**
   * Update the vehicle's first registration date.
   * - flag ON  -> PATCH /v1/vehicles/{vin}/first-registration
   * - flag OFF -> legacy addUserVehicle upsert (drop `vehicle` once legacy is gone)
   */
  public updateFirstRegistration (
    vehicle: NewVehicleDTO,
    firstRegistration: string | null
  ): Observable<VehicleFirstRegistrationDto | NewVehicleDTO> {
    if (this.isNewOwnershipApiEnabled() && vehicle.vin != null) {
      return this.http.patch<VehicleFirstRegistrationDto>(
        `${this.apiUrl()}/v1/vehicles/${vehicle.vin}/first-registration`,
        { firstRegistration }
      )
    }
    return this.addUserVehicle(vehicle)
  }

  // New ownership api
  /**
   * This service does two things depending on isNewOwnershipEnabled flag
   * - if true -> it will call new endpoints and map them to VehicleDto
   * - if false -> it will remap old props to new ones
   */
  fetchAdditionalVehicleProperties (
    vehicle: VehicleDTO | LegacyVehicleDTOExtended
  ): Observable<NewVehicleDTO> {
    // If vehicle is inactive we should not call load or details endpoints, this should be revised in further development with new endpoints
    if (this.isNewOwnershipApiEnabled() && vehicle.active) {
      return this.fetchNewVehicleDetails(vehicle)
    }

    return of({
      ...vehicle,
      details: fromLegacyVehicleDetailsToVehicleSpecsDTO(vehicle),
      loan: fromLegacyLoanToVehicleLoan(vehicle.loan)
    })
  }

  private fetchNewVehicleDetails (
    vehicle: VehicleDTO | LegacyVehicleDTOExtended | NewVehicleDTO
  ): Observable<NewVehicleDTO> {
    return forkJoin({
      details: this.getV1VehicleDetails(vehicle.vin).pipe(
        catchError((error: HttpErrorResponse) => {
          // 404 = vehicle has no details -> treat as "no data", not an error
          if (error.status === 404) {
            return of(null)
          }
          return throwError(() => error)
        })
      ),
      loan: this.getV1VehicleLoan(vehicle.vin).pipe(
        catchError((error: HttpErrorResponse) => {
          // 404 = vehicle has no loan/leasing -> treat as "no data", not an error
          if (error.status === 404) {
            return of(null)
          }
          return throwError(() => error)
        })
      )
    }).pipe(
      map(({ details, loan }) => ({
        ...vehicle,
        details,
        loan,
        // details wins, the ownership value is the fallback; both are date-only strings
        firstRegistration: details?.firstRegistration ?? vehicle.firstRegistration
      }))
    )
  }
}
